export type Lang = 'en' | 'es'

export type ServiceIcon = 'wash' | 'vacuum' | 'sparkle' | 'truck'
export type ValueIcon = 'medal' | 'spray' | 'check' | 'clock'
export type StepIcon = 'calendar' | 'pin' | 'sparkle'

const en = {
  announcement: '🔥 Professional Deep Car Cleaning • Same Day Service Available • Se Habla Español',
  nav: {
    home: 'Home',
    services: 'Services',
    gallery: 'Gallery',
    about: 'About',
    faq: 'FAQ',
    contact: 'Contact',
    book: 'Book Now',
    menu: 'Open menu',
    close: 'Close menu',
    language: 'Switch to Spanish',
  },
  hero: {
    eyebrow: 'Exterior & interior specialists',
    title: 'Professional Deep Car Cleaning Services',
    subtitle:
      'Expert interior & exterior detailing • Flawless results • Same-day appointments available',
    primaryCta: 'Schedule Appointment',
    secondaryCta: 'Call: +1 (415) 368-4846',
    badges: ['Expert Team', '100+ Happy Customers', 'Same-Day Service', 'Se Habla Español'],
    imageAlt: 'los BROTHERS detailing banner showing a freshly cleaned vehicle',
  },
  services: {
    kicker: 'Services',
    title: 'Two service tiers, one standard of work',
    subtitle: 'Every package is finished by hand, inside and out.',
    items: [
      {
        icon: 'wash' as ServiceIcon,
        name: 'Exterior & Interior Basic',
        description:
          'Professional exterior wash with wheel cleaning and tire shine. Complete interior vacuuming and air circulation system refresh.',
        features: [
          'Exterior wash and rinse',
          'Wheel and tire cleaning',
          'Tire shine application',
          'Interior vacuuming',
          'Air vent and dashboard detailing',
          'Interior air circulation refresh',
        ],
        duration: '~1 hour',
        cta: 'Book Now',
        featured: false,
      },
      {
        icon: 'sparkle' as ServiceIcon,
        name: 'Complete Interior & Exterior Detail',
        description:
          'Full exterior wash, wheel detailing and tire application. Complete interior detailing: seat cleaning, carpet shampooing, ceiling cleaning and final polish.',
        features: [
          'Complete exterior wash and rinse',
          'Professional wheel cleaning',
          'Tire shine and protectant application',
          'Seat and upholstery cleaning',
          'Carpet and floor mat shampooing',
          'Ceiling and trim detailing',
          'Interior air circulation refresh',
          'Final protective polish',
        ],
        duration: '~2 hours',
        cta: 'Book Now',
        featured: true,
      },
    ],
    mostBooked: 'Most popular',
  },
  why: {
    kicker: 'Why los BROTHERS',
    title: 'Detailing done the way we would do our own car',
    items: [
      {
        icon: 'medal' as ValueIcon,
        title: 'Expert Detailing',
        description: 'Years of professional experience with proven, repeatable results.',
      },
      {
        icon: 'spray' as ValueIcon,
        title: 'Premium Products',
        description: 'Professional-grade products that are safe for all vehicle types.',
      },
      {
        icon: 'check' as ValueIcon,
        title: 'Attention to Detail',
        description: 'We treat your car like it is our own, down to the last vent.',
      },
      {
        icon: 'clock' as ValueIcon,
        title: 'Fast & Reliable',
        description: 'Same-day appointments and quick, predictable turnaround times.',
      },
    ],
  },
  how: {
    kicker: 'How it works',
    title: 'Three steps to a spotless car',
    steps: [
      {
        icon: 'calendar' as StepIcon,
        title: 'Book online or call',
        description: 'Schedule your appointment in seconds by phone, WhatsApp or the form below.',
      },
      {
        icon: 'pin' as StepIcon,
        title: 'Drop off or on-site',
        description: 'We come to you, or you drop off — whichever fits your day.',
      },
      {
        icon: 'sparkle' as StepIcon,
        title: 'Enjoy pristine results',
        description: 'Pick up a spotless, fully detailed car that is ready to impress.',
      },
    ],
  },
  gallery: {
    kicker: 'Portfolio',
    title: 'See The Difference — Before/After & In Action',
    subtitle:
      'Real transformations from real customers. Drag to compare, or open "Wash In Action" to see our crew at work.',
    tabs: {
      beforeAfter: 'Before & After',
      inAction: 'Wash In Action',
    },
    play: 'Play video',
    view: 'View photo',
    close: 'Close',
    before: 'Before',
    after: 'After',
    hint: 'Drag to compare',
    sliderLabel: 'Before and after comparison slider',
    more: '39+ transformations on Instagram',
  },
  testimonials: {
    kicker: 'Reviews',
    title: 'What Our Customers Say',
    subtitle: 'Real feedback from drivers across the area.',
    items: [
      { quote: 'Excelente trabajo! My truck looks brand new inside and out.', name: 'Carlos M.', meta: 'RAM 1500 · Full Service' },
      { quote: 'Fast, professional, and affordable. Highly recommend!', name: 'Maria L.', meta: 'Honda CR-V · Interior Detail' },
      { quote: 'Best car detailing service in the area, hands down.', name: 'James D.', meta: 'Tesla Model 3 · Exterior Wash' },
      { quote: 'They came to my office and finished before my next meeting.', name: 'Andrea P.', meta: 'Mobile Service' },
      { quote: 'Dejaron mi carro impecable, súper amables y puntuales.', name: 'Luis R.', meta: 'Toyota Tacoma · Full Service' },
    ],
    prev: 'Previous review',
    next: 'Next review',
  },
  about: {
    kicker: 'About our team',
    title: 'Two brothers, one standard: spotless',
    body: [
      'los BROTHERS was founded by two brothers who grew up washing cars in the driveway and turned that obsession into a professional detailing service.',
      'Every job is done by hand, with premium products and a checklist we never skip — because the finish is the only thing that speaks for us.',
    ],
    bullets: [
      '10+ years combined professional detailing experience',
      '100+ satisfied customers across the region',
      'Bilingual team — English & Spanish',
      'Committed to excellence in every detail',
    ],
    imageAlt: 'The los BROTHERS team detailing a car in the shop',
    stats: [
      { value: '10+', label: 'Years experience' },
      { value: '100+', label: 'Happy customers' },
      { value: '39+', label: 'Transformations' },
    ],
  },
  area: {
    kicker: 'Service area',
    title: 'Mobile service or drop-off',
    subtitle: 'Available for mobile service at your home or office, or drop-off at our location.',
    radius: '15–20 mile service radius',
    radiusNote: 'Outside the radius? Call us — we often make it work.',
    hoursTitle: 'Hours',
    hours: 'Mon–Sat 8am–6pm · Sun by appointment',
    callUs: 'Call us',
    whatsapp: 'WhatsApp',
    mapTitle: 'Map of the los BROTHERS service area',
  },
  faq: {
    kicker: 'FAQ',
    title: 'Frequently Asked Questions',
    subtitle: 'Everything drivers ask us before their first appointment.',
    items: [
      {
        q: 'How long does a deep clean take?',
        a: 'The Exterior & Interior Basic package takes about 1 hour. The Complete Interior & Exterior Detail takes about 2 hours.',
      },
      {
        q: 'Do you use eco-friendly products?',
        a: 'Yes. All of our products are professional grade and safe for your car and the environment.',
      },
      {
        q: 'Can you come to my location?',
        a: 'Yes! We offer mobile service throughout the area, at your home or workplace.',
      },
      {
        q: 'How much does it cost?',
        a: 'Pricing depends on the vehicle size, condition and the package you choose. Call or WhatsApp us and we will give you an exact quote in minutes.',
      },
      {
        q: 'Do you accept walk-ins?',
        a: 'We recommend booking ahead, but same-day appointments are often available — just call.',
      },
      { q: 'Are you insured?', a: 'Yes, we are fully insured and bonded for your peace of mind.' },
      {
        q: 'What payment methods do you accept?',
        a: 'Cash, Venmo, PayPal, credit cards, and direct bank transfer.',
      },
      {
        q: 'How do I book?',
        a: 'Call +1 (415) 368-4846, send a WhatsApp message, or use the booking form on this page.',
      },
    ],
  },
  booking: {
    kicker: 'Booking',
    title: 'Schedule Your Appointment',
    subtitle: 'Fill out the form below or call us directly — we answer fast.',
    fields: {
      name: 'Full name',
      phone: 'Phone number',
      email: 'Email',
      service: 'Service type',
      vehicle: 'Vehicle make / model',
      date: 'Preferred date',
      time: 'Preferred time',
      message: 'Notes',
      optional: 'optional',
      required: 'required',
    },
    placeholders: {
      name: 'Juan Pérez',
      phone: '(415) 000-0000',
      email: 'you@email.com',
      vehicle: 'RAM 1500, 2021',
      message: 'Anything we should know about the vehicle?',
    },
    serviceOptions: [
      'Exterior & Interior Basic (~1 hour)',
      'Complete Interior & Exterior Detail (~2 hours)',
    ],
    selectService: 'Select a service',
    callback: 'Prefer phone call confirmation',
    submit: 'Request Appointment',
    submitting: 'Sending…',
    successTitle: 'Request received',
    successBody: 'We will confirm your appointment shortly. For anything urgent, call or WhatsApp us.',
    successReset: 'Send another request',
    errorRequired: 'Please add your name and phone number so we can confirm.',
    orCall: 'Prefer to talk? Reach us directly:',
  },
  instagram: {
    kicker: 'Instagram',
    title: 'Follow us on Instagram',
    subtitle: 'Limpieza de carro profunda exterior e interior número 1.',
    cta: 'Follow @cleaning10127',
    posts: 'Posts',
    followers: 'Followers',
    following: 'Following',
    tileAlt: 'Instagram post from los BROTHERS',
  },
  footer: {
    about: 'Professional deep car cleaning services — exterior & interior specialists.',
    quickLinks: 'Quick links',
    servicesTitle: 'Services',
    services: [
      'Exterior & Interior Basic',
      'Complete Interior & Exterior Detail',
      'Mobile Service',
    ],
    contactTitle: 'Contact & hours',
    hours: 'Mon–Sat 8am–6pm · Sun by appointment',
    sameDay: 'Same-day service available',
    rights: 'All rights reserved.',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
  },
  floating: {
    whatsapp: 'WhatsApp us: +1 (415) 368-4846',
    whatsappShort: 'WhatsApp',
    call: 'Call',
    close: 'Dismiss message',
  },
}

export type Copy = typeof en

const es: Copy = {
  announcement: '🔥 Limpieza Profunda Profesional • Servicio el Mismo Día • Se Habla Español',
  nav: {
    home: 'Inicio',
    services: 'Servicios',
    gallery: 'Galería',
    about: 'Nosotros',
    faq: 'Preguntas',
    contact: 'Contacto',
    book: 'Reservar',
    menu: 'Abrir menú',
    close: 'Cerrar menú',
    language: 'Cambiar a inglés',
  },
  hero: {
    eyebrow: 'Especialistas en exterior e interior',
    title: 'Limpieza Profunda Profesional para tu Carro',
    subtitle:
      'Detallado experto interior y exterior • Resultados impecables • Citas el mismo día',
    primaryCta: 'Agendar Cita',
    secondaryCta: 'Llamar: +1 (415) 368-4846',
    badges: ['Equipo Experto', '100+ Clientes Felices', 'Servicio el Mismo Día', 'Se Habla Español'],
    imageAlt: 'Banner de los BROTHERS con un vehículo recién detallado',
  },
  services: {
    kicker: 'Servicios',
    title: 'Dos paquetes, un mismo estándar de trabajo',
    subtitle: 'Todo el trabajo se hace a mano, por dentro y por fuera.',
    items: [
      {
        icon: 'wash',
        name: 'Básico Exterior e Interior',
        description:
          'Lavado exterior profesional con limpieza de rines y brillo de llantas. Aspirado interior completo y refresco del sistema de ventilación.',
        features: [
          'Lavado y enjuague exterior',
          'Limpieza de rines y llantas',
          'Aplicación de brillo de llantas',
          'Aspirado interior',
          'Detallado de ventilas y tablero',
          'Refresco del sistema de ventilación',
        ],
        duration: '~1 hora',
        cta: 'Reservar',
        featured: false,
      },
      {
        icon: 'sparkle',
        name: 'Detallado Completo Interior y Exterior',
        description:
          'Lavado exterior completo, detallado de rines y aplicación en llantas. Detallado interior completo: lavado de asientos, shampoo de alfombras, limpieza de techo y pulido final.',
        features: [
          'Lavado y enjuague exterior completo',
          'Limpieza profesional de rines',
          'Brillo y protector de llantas',
          'Lavado de asientos y tapicería',
          'Shampoo de alfombras y tapetes',
          'Detallado de techo y molduras',
          'Refresco del sistema de ventilación',
          'Pulido protector final',
        ],
        duration: '~2 horas',
        cta: 'Reservar',
        featured: true,
      },
    ],
    mostBooked: 'Más popular',
  },
  why: {
    kicker: 'Por qué los BROTHERS',
    title: 'Detallado como si fuera nuestro propio carro',
    items: [
      {
        icon: 'medal',
        title: 'Detallado Experto',
        description: 'Años de experiencia profesional con resultados comprobados.',
      },
      {
        icon: 'spray',
        title: 'Productos Premium',
        description: 'Productos de grado profesional, seguros para todo tipo de vehículo.',
      },
      {
        icon: 'check',
        title: 'Atención al Detalle',
        description: 'Tratamos tu carro como el nuestro, hasta la última rejilla.',
      },
      {
        icon: 'clock',
        title: 'Rápido y Confiable',
        description: 'Citas el mismo día y tiempos de entrega puntuales.',
      },
    ],
  },
  how: {
    kicker: 'Cómo funciona',
    title: 'Tres pasos para un carro impecable',
    steps: [
      {
        icon: 'calendar',
        title: 'Reserva o llama',
        description: 'Agenda tu cita en segundos por teléfono, WhatsApp o el formulario.',
      },
      {
        icon: 'pin',
        title: 'Vamos a ti o nos visitas',
        description: 'Servicio móvil a domicilio o entrega en nuestro local, tú decides.',
      },
      {
        icon: 'sparkle',
        title: 'Disfruta el resultado',
        description: 'Recibe un carro impecable, detallado y listo para impresionar.',
      },
    ],
  },
  gallery: {
    kicker: 'Portafolio',
    title: 'Mira la Diferencia — Antes/Después y en Acción',
    subtitle:
      'Transformaciones reales de clientes reales. Arrastra para comparar, o abre "En Acción" para ver a nuestro equipo trabajando.',
    tabs: {
      beforeAfter: 'Antes y Después',
      inAction: 'En Acción',
    },
    play: 'Reproducir video',
    view: 'Ver foto',
    close: 'Cerrar',
    before: 'Antes',
    after: 'Después',
    hint: 'Arrastra para comparar',
    sliderLabel: 'Control de comparación antes y después',
    more: '39+ transformaciones en Instagram',
  },
  testimonials: {
    kicker: 'Reseñas',
    title: 'Lo Que Dicen Nuestros Clientes',
    subtitle: 'Comentarios reales de conductores de la zona.',
    items: [
      { quote: '¡Excelente trabajo! Mi troca quedó como nueva por dentro y por fuera.', name: 'Carlos M.', meta: 'RAM 1500 · Servicio Completo' },
      { quote: 'Rápidos, profesionales y accesibles. ¡Muy recomendados!', name: 'Maria L.', meta: 'Honda CR-V · Interior' },
      { quote: 'El mejor servicio de detallado de la zona, sin duda.', name: 'James D.', meta: 'Tesla Model 3 · Exterior' },
      { quote: 'Llegaron a mi oficina y terminaron antes de mi siguiente junta.', name: 'Andrea P.', meta: 'Servicio Móvil' },
      { quote: 'Dejaron mi carro impecable, súper amables y puntuales.', name: 'Luis R.', meta: 'Toyota Tacoma · Completo' },
    ],
    prev: 'Reseña anterior',
    next: 'Siguiente reseña',
  },
  about: {
    kicker: 'Nuestro equipo',
    title: 'Dos hermanos, un estándar: impecable',
    body: [
      'los BROTHERS nació de dos hermanos que crecieron lavando carros en la cochera y convirtieron esa obsesión en un servicio de detallado profesional.',
      'Cada trabajo se hace a mano, con productos premium y una lista de revisión que nunca saltamos — porque el acabado es lo único que habla por nosotros.',
    ],
    bullets: [
      '10+ años de experiencia profesional combinada',
      '100+ clientes satisfechos en la región',
      'Equipo bilingüe — inglés y español',
      'Compromiso con la excelencia en cada detalle',
    ],
    imageAlt: 'El equipo de los BROTHERS detallando un carro en el taller',
    stats: [
      { value: '10+', label: 'Años de experiencia' },
      { value: '100+', label: 'Clientes felices' },
      { value: '39+', label: 'Transformaciones' },
    ],
  },
  area: {
    kicker: 'Área de servicio',
    title: 'Servicio móvil o entrega',
    subtitle: 'Servicio móvil en tu casa u oficina, o entrega del vehículo en nuestro local.',
    radius: 'Radio de servicio de 15 a 20 millas',
    radiusNote: '¿Estás fuera del radio? Llámanos, casi siempre lo resolvemos.',
    hoursTitle: 'Horario',
    hours: 'Lun–Sáb 8am–6pm · Dom con cita',
    callUs: 'Llámanos',
    whatsapp: 'WhatsApp',
    mapTitle: 'Mapa del área de servicio de los BROTHERS',
  },
  faq: {
    kicker: 'Preguntas',
    title: 'Preguntas Frecuentes',
    subtitle: 'Todo lo que nos preguntan antes de la primera cita.',
    items: [
      {
        q: '¿Cuánto tarda una limpieza profunda?',
        a: 'El paquete Básico Exterior e Interior toma alrededor de 1 hora. El Detallado Completo toma alrededor de 2 horas.',
      },
      {
        q: '¿Usan productos ecológicos?',
        a: 'Sí. Todos nuestros productos son de grado profesional y seguros para tu carro y el medio ambiente.',
      },
      {
        q: '¿Pueden ir a mi ubicación?',
        a: '¡Sí! Ofrecemos servicio móvil en toda la zona, en tu casa o trabajo.',
      },
      {
        q: '¿Cuánto cuesta?',
        a: 'Depende del tamaño y condición del vehículo y del paquete que elijas. Llámanos o escríbenos por WhatsApp y te damos una cotización exacta en minutos.',
      },
      {
        q: '¿Aceptan clientes sin cita?',
        a: 'Recomendamos agendar, pero muchas veces hay citas el mismo día — solo llámanos.',
      },
      { q: '¿Están asegurados?', a: 'Sí, contamos con seguro completo para tu tranquilidad.' },
      {
        q: '¿Qué métodos de pago aceptan?',
        a: 'Efectivo, Venmo, PayPal, tarjetas de crédito y transferencia bancaria.',
      },
      {
        q: '¿Cómo reservo?',
        a: 'Llama al +1 (415) 368-4846, escríbenos por WhatsApp o usa el formulario de esta página.',
      },
    ],
  },
  booking: {
    kicker: 'Reservación',
    title: 'Agenda tu Cita',
    subtitle: 'Llena el formulario o llámanos directamente — contestamos rápido.',
    fields: {
      name: 'Nombre completo',
      phone: 'Teléfono',
      email: 'Correo',
      service: 'Tipo de servicio',
      vehicle: 'Marca / modelo del vehículo',
      date: 'Fecha preferida',
      time: 'Hora preferida',
      message: 'Notas',
      optional: 'opcional',
      required: 'obligatorio',
    },
    placeholders: {
      name: 'Juan Pérez',
      phone: '(415) 000-0000',
      email: 'tu@correo.com',
      vehicle: 'RAM 1500, 2021',
      message: '¿Algo que debamos saber del vehículo?',
    },
    serviceOptions: [
      'Básico Exterior e Interior (~1 hora)',
      'Detallado Completo Interior y Exterior (~2 horas)',
    ],
    selectService: 'Selecciona un servicio',
    callback: 'Prefiero confirmación por llamada',
    submit: 'Solicitar Cita',
    submitting: 'Enviando…',
    successTitle: 'Solicitud recibida',
    successBody: 'Confirmaremos tu cita en breve. Si es urgente, llámanos o escríbenos por WhatsApp.',
    successReset: 'Enviar otra solicitud',
    errorRequired: 'Agrega tu nombre y teléfono para poder confirmar.',
    orCall: '¿Prefieres hablar? Contáctanos directo:',
  },
  instagram: {
    kicker: 'Instagram',
    title: 'Síguenos en Instagram',
    subtitle: 'Limpieza de carro profunda exterior e interior número 1.',
    cta: 'Seguir @cleaning10127',
    posts: 'Publicaciones',
    followers: 'Seguidores',
    following: 'Siguiendo',
    tileAlt: 'Publicación de Instagram de los BROTHERS',
  },
  footer: {
    about: 'Servicio profesional de limpieza profunda — especialistas en exterior e interior.',
    quickLinks: 'Enlaces',
    servicesTitle: 'Servicios',
    services: [
      'Básico Exterior e Interior',
      'Detallado Completo Interior y Exterior',
      'Servicio Móvil',
    ],
    contactTitle: 'Contacto y horario',
    hours: 'Lun–Sáb 8am–6pm · Dom con cita',
    sameDay: 'Servicio el mismo día disponible',
    rights: 'Todos los derechos reservados.',
    privacy: 'Aviso de Privacidad',
    terms: 'Términos del Servicio',
  },
  floating: {
    whatsapp: 'Escríbenos por WhatsApp: +1 (415) 368-4846',
    whatsappShort: 'WhatsApp',
    call: 'Llamar',
    close: 'Cerrar mensaje',
  },
}

export const copy: Record<Lang, Copy> = { en, es }
